import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO, addDays } from "date-fns";
import {
    FileText,
    Users as UsersIcon,
    Globe,
    Database,
    ChevronDown
} from 'lucide-react';
import { getActivityLogs, getModelQueryLogs } from '../../../api';

const websiteRequestsData = [
    { date: "2025-02-19", requests: 420 },
    { date: "2025-02-20", requests: 430 },
    { date: "2025-02-21", requests: 415 },
    { date: "2025-02-22", requests: 425 },
    { date: "2025-02-23", requests: 420 },
    { date: "2025-02-24", requests: 435 },
    { date: "2025-02-25", requests: 440 },
    { date: "2025-02-26", requests: 450 },
    { date: "2025-02-27", requests: 445 },
    { date: "2025-02-28", requests: 455 },
    { date: "2025-03-01", requests: 460 },
    { date: "2025-03-02", requests: 465 },
    { date: "2025-03-03", requests: 470 },
    { date: "2025-03-04", requests: 475 },
    { date: "2025-03-05", requests: 480 },
    { date: "2025-03-06", requests: 485 },
    { date: "2025-03-07", requests: 490 },
    { date: "2025-03-08", requests: 495 },
    { date: "2025-03-09", requests: 500 },
    { date: "2025-03-10", requests: 505 },
    { date: "2025-03-11", requests: 510 },
    { date: "2025-03-12", requests: 515 },
    { date: "2025-03-13", requests: 520 },
    { date: "2025-03-14", requests: 525 },
    { date: "2025-03-15", requests: 530 },
    { date: "2025-03-16", requests: 535 },
    { date: "2025-03-17", requests: 540 },
    { date: "2025-03-18", requests: 545 },
    { date: "2025-03-19", requests: 550 },
    { date: "2025-03-20", requests: 555 },
    { date: "2025-03-21", requests: 560 },
    { date: "2025-03-22", requests: 565 },
    { date: "2025-03-23", requests: 570 },
    { date: "2025-03-24", requests: 575 },
    { date: "2025-03-25", requests: 580 }
];

const ActivityLogs = () => {
    const { isDarkTheme } = useTheme();
    const [selectedModel, setSelectedModel] = useState('OG-Drilling');
    const [timeRange, setTimeRange] = useState('7');
    const [totalUserCount, setTotalUserCount] = useState(0);
    const [totalPdfUploads, setTotalPdfUploads] = useState(0);
    const [totalWebSearches, setTotalWebSearches] = useState(0);
    const [modelData, setModelData] = useState({});

    // API for activity logs totals
    useEffect(() => {
        const fetchDashboardTotals = async () => {
            try {
                const response = await getActivityLogs();
                const totals = response.totals
                setTotalUserCount(totals.totalUsers);
                setTotalPdfUploads(totals.totalPdfUploads);
                setTotalWebSearches(totals.totalWebSearches);
            } catch (error) {
                console.error("Error fetching dashboard totals:", error);
            }
        };
    
        fetchDashboardTotals();
    }, []);

    // API to get queries data for each model chart
    useEffect(() => {
        const fetchData = async () => {
            try {
            const response = await getModelQueryLogs();
            const logs = response.logs;
            setModelData(logs);
            } catch (error) {
            console.error("Failed to fetch model query logs:", error);
            }
        };
        fetchData();
        }, []);
    

    // Modify data filtering to support All Time and selective date display
    const filterDataWithMaxPoints = (data, maxPoints = 11) => {
        // If All Time, create points with 15-day gaps
        if (timeRange === 'all') {
            if (data.length <= maxPoints) return data;

            const result = [];
            const startDate = parseISO(data[0].date);
            const endDate = parseISO(data[data.length - 1].date);

            // Calculate total days in range
            const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
            const interval = Math.max(1, Math.floor(totalDays / (maxPoints - 1)));

            // Helper to find closest data point to a target date
            const findClosestDataPoint = (targetDate) => {
                return data.reduce((prev, curr) => {
                    const prevDiff = Math.abs(parseISO(prev.date) - targetDate);
                    const currDiff = Math.abs(parseISO(curr.date) - targetDate);
                    return currDiff < prevDiff ? curr : prev;
                });
            };

            // Always include first point
            result.push(data[0]);

            // Add intermediate points
            for (let i = 1; i < maxPoints - 1; i++) {
                const targetDate = addDays(startDate, i * interval);
                const closestPoint = findClosestDataPoint(targetDate);
                if (closestPoint.date !== result[result.length - 1].date) {
                    result.push(closestPoint);
                }
            }

            // Always include last point if not already included
            if (result[result.length - 1].date !== data[data.length - 1].date) {
                result.push(data[data.length - 1]);
            }

            return result;
        }

        // For specific time ranges
        const numericRange = parseInt(timeRange);
        const sourceData = numericRange === 30
            ? data.filter((_, index) => index % 3 === 0)
            : data;

        return sourceData.slice(-numericRange);
    };

    // Filter data based on time range for the selected model (for chart)
    const filteredModelData = useMemo(() => {
        if (!modelData || !modelData[selectedModel]) return [];
        const data = modelData[selectedModel];
        return Array.isArray(data) ? filterDataWithMaxPoints(data) : [];
    }, [selectedModel, timeRange, modelData]);

    // Filter website requests data based on time range (for chart)
    const filteredWebsiteData = useMemo(() => {
        return filterDataWithMaxPoints(websiteRequestsData);
    }, [timeRange]);

    // Render x-axis with responsive formatting
    const renderResponsiveXAxis = (isMobile) => {
        return (
            <XAxis
                dataKey="date"
                tick={{ fill: isDarkTheme ? 'white' : 'black', fontSize: isMobile ? 10 : 12 }}
                tickFormatter={(tick) => {
                    const date = new Date(tick);
                    return isMobile
                        ? format(date, "MMM d")
                        : format(date, "MMMM dd");
                }}
                interval="preserveStartEnd"
            />
        );
    };

    // Render website requests chart
    const renderWebsiteRequestsChart = () => {
        const totalRequests = filteredWebsiteData.reduce((sum, item) => sum + item.requests, 0);
        return (
            <div className={`p-4 rounded-lg ${isDarkTheme ? 'bg-gray-800' : 'bg-white'} shadow-md mb-6`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-lg font-semibold ${isDarkTheme ? 'text-white' : 'text-gray-900'}`}>
                        Website Requests
                    </h3>
                    <span className={`text-sm ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`}>
                        Total Requests: {totalRequests}
                    </span>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={filteredWebsiteData}>
                        {renderResponsiveXAxis(window.innerWidth < 768)}
                        <YAxis
                            tick={{
                                fill: isDarkTheme ? 'white' : 'black',
                                fontSize: window.innerWidth < 768 ? 10 : 12
                            }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: isDarkTheme ? '#374151' : '#f9fafb',
                                color: isDarkTheme ? 'white' : 'black'
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="requests"
                            stroke="#10b981"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                            className="hover:opacity-80 transition-opacity"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Render model queries chart for the selected model
    const renderModelChart = () => {
        const totalQueries = filteredModelData.reduce((sum, item) => sum + item.queries, 0);
        return (
            <div className={`p-4 rounded-lg ${isDarkTheme ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className={`text-xl font-semibold ${isDarkTheme ? 'text-white' : 'text-gray-900'}`}>
                            {selectedModel} Queries
                        </h3>
                        <span className={`text-base ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total Queries: {totalQueries}
                        </span>
                    </div>
                    {/* Model selector */}
                    <div className="relative w-40">
                        <select
                            value={selectedModel}
                            onChange={(e) => setSelectedModel(e.target.value)}
                            className={`w-full appearance-none px-3 py-2 pr-10 rounded-lg border ${isDarkTheme ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-amber-500`}
                        >
                            <option value="OG-Drilling">OG-Drilling</option>
                            <option value="OG-Reservoir">OG-Reservoir</option>
                            <option value="OG-Production">OG-Production</option>
                        </select>
                        <ChevronDown
                            className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'
                                }`}
                            size={20}
                        />
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={filteredModelData}>
                        {renderResponsiveXAxis(window.innerWidth < 768)}
                        <YAxis
                            tick={{
                                fill: isDarkTheme ? 'white' : 'black',
                                fontSize: window.innerWidth < 768 ? 10 : 12
                            }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: isDarkTheme ? '#374151' : '#f9fafb',
                                color: isDarkTheme ? 'white' : 'black'
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="queries"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                            className="hover:opacity-80 transition-opacity"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Stat card component
    const StatCard = ({ icon: Icon, title, value }) => (
        <div className={`p-4 rounded-lg flex items-center space-x-4 ${isDarkTheme ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
            <div className={`p-3 rounded-full ${isDarkTheme ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <Icon className={`${isDarkTheme ? 'text-amber-400' : 'text-amber-500'}`} size={24} />
            </div>
            <div>
                <p className={`text-sm ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
                <p className={`text-xl font-bold ${isDarkTheme ? 'text-white' : 'text-gray-900'}`}>
                    {value.toLocaleString()}
                </p>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className={`text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent`}>
                    Activity Logs
                </h1>
                <p className={`text-sm mt-1 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                    Detailed insights into system usage and performance
                </p>
            </div>

            {/* Primary Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard icon={UsersIcon} title="Total Users" value={totalUserCount} />
                <StatCard icon={FileText} title="PDF Uploads" value={totalPdfUploads} />
                <StatCard icon={Globe} title="Web Searches" value={totalWebSearches} />
                <StatCard
                    icon={Database}
                    title="Total Model Queries"
                    value={
                        (modelData['OG-Drilling'] || []).reduce((sum, item) => sum + item.queries, 0) +
                        (modelData['OG-Reservoir'] || []).reduce((sum, item) => sum + item.queries, 0) +
                        (modelData['OG-Production'] || []).reduce((sum, item) => sum + item.queries, 0)
                    }
                />
            </div>

            {/* Global Time Range Control */}
            <div className="w-full md:w-52">
                <label className={`block text-sm mb-1 ${isDarkTheme ? 'text-gray-300' : 'text-gray-600'}`}>
                    Time Range
                </label>
                <div className="relative">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className={`w-full appearance-none px-3 py-2 pr-10 rounded-lg border ${isDarkTheme ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-amber-500`}
                    >
                        <option value="7">Last 7 Days</option>
                        <option value="11">Last 11 Days</option>
                        <option value="30">Last 30 Days</option>
                        <option value="all">All Time</option>
                    </select>
                    <ChevronDown
                        className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'
                            }`}
                        size={20}
                    />
                </div>
            </div>

            {/* Website Requests Chart */}
            {renderWebsiteRequestsChart()}

            {/* Model Queries Chart with Model Selector */}
            {renderModelChart()}
        </div>
    );
};

export default ActivityLogs;
